import { Platform } from 'react-native'
import { axiosInstance, getApiUrl, getAuthHeaders } from '../Middleware'
import { CustomFile, Document } from './types'
import { Buffer } from 'buffer';
import dayjs from 'dayjs';

export class DocumentApi {
  public static loadDocument = (complement?: string) : Promise<Document []> => {
    let url = '/personnel/personnel/documents';

    if (complement) {     
      url += complement;
    }
    return axiosInstance.get(url)
      .then(response => response.data);
  }

  public static downloadDocument = async (identifiantPJ: number, silent?: boolean) : Promise<string> => {
    // Configurer les headers par défaut
    axiosInstance.defaults.headers.common.Accept = '';

    const resp = await axiosInstance.get(`/personnel/personnel/documents/${identifiantPJ}/download`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Content-Description': 'File Transfer',
        'Content-Type': 'application/pdf',
        'Content-Disposition': 'attachment; filename=""',
        'Content-Transfer-Encoding': 'binary',
        'Accept': undefined,
      },
      responseType: 'arraybuffer',
      ...(silent ? { silent: true } : {}),
    } as any);

    // Convertir ArrayBuffer en format utilisable selon la plateforme
    const arrayBuffer = resp.data;
    
    if (Platform.OS === 'web') {
      // Web : créer une URL blob
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob);
      return fileURL;
    } else {
      // Mobile : créer une data URI pour react-native-pdf ou Image
      const contentType = (resp.headers?.['content-type'] as string)?.split(';')[0] || 'application/octet-stream';
      const base64String = Buffer.from(arrayBuffer).toString('base64');
      const dataUri = `data:${contentType};base64,${base64String}`;
      return dataUri;
    }
  }

  public static postDocument = async (
    fichier: File | CustomFile,
    champs: Partial<Document>
  ): Promise<Document> => {
    const today = dayjs().format('YYYY-MM-DD');
    const fileName = 'name' in fichier ? fichier.name : 'document';

    const defaultValues: Document = {
      identifiant: 0,
      identifiantEntite: 0,
      identifiantLogin: 0,
      libelle: '',
      extension: '',
      type: '',
      code: '',
      identifiantOrigine: 0,
      identifiantReference: 0,
      origineProduit: 0,
      periodeDemat: '',
      identifiantStockageObjet: '',
      insererDansGed: 0,
      masquePortail: 0,
      bpPartiel: 0,
      nomenclature: '',
      controle: 0,
      notifEsag: 0,
      coffreFort: 0,
      dateEnvoiNotifEsag: '',
      dateCreation: today,
      dateModification: today,
      description: '',
    };

    const propsJson = JSON.stringify({
      ...defaultValues,
      ...champs,
      libelle: fileName,
      extension: `.${fileName.split('.').pop()}`,
      dateCreation: today,
      dateModification: today,
    });

    if (Platform.OS !== 'web') {
      // Mobile (Android/iOS) : utiliser react-native-blob-util pour uploader
      // les fichiers content:// ou file:// — Axios/XHR ne gère pas ces URIs nativement
      const RNBlobUtil = require('react-native-blob-util').default;
      const authHeaders = getAuthHeaders();
      const fileUri = (fichier as CustomFile).uri;
      const fileType = fichier.type || 'image/jpeg';

      let documentPart: any;
      let tmpFilePath: string | null = null;
      if (fileUri.startsWith('data:')) {
        // Data URI : décoder le base64 et écrire dans un fichier temporaire
        // car RNBlobUtil.fetch envoie les chaînes "data" comme texte brut,
        // non comme binaire — il faut passer par wrap() d'un vrai fichier.
        const commaIndex = fileUri.indexOf(',');
        const base64Data = fileUri.substring(commaIndex + 1);
        const ext = fileName.split('.').pop() || 'bin';
        tmpFilePath = `${RNBlobUtil.fs.dirs.CacheDir}/tmp_upload_${Date.now()}.${ext}`;
        await RNBlobUtil.fs.writeFile(tmpFilePath, base64Data, 'base64');
        documentPart = { name: 'document', filename: fileName, type: fileType, data: RNBlobUtil.wrap(tmpFilePath) };
      } else {
        // URI fichier (file://, content://) : utiliser RNBlobUtil.wrap
        documentPart = { name: 'document', filename: fileName, type: fileType, data: RNBlobUtil.wrap(fileUri) };
      }

      const response = await RNBlobUtil.fetch(
        'POST',
        `${getApiUrl()}/personnel/personnel/documents`,
        {
          ...authHeaders,
          'Content-Type': 'multipart/form-data',
        },
        [
          { name: 'props', data: propsJson },
          documentPart,
        ]
      );
      // Nettoyer le fichier temporaire éventuel (data URI)
      if (tmpFilePath) {
        RNBlobUtil.fs.unlink(tmpFilePath).catch(() => {});
      }
      return JSON.parse(response.data);
    }

    // Web : FormData + Axios (boundary généré automatiquement)
    const formData = new FormData();
    formData.append('props', propsJson);

    if ((fichier as CustomFile)?.uri?.startsWith('data:')) {
      // Convertir data URL en File
      const fetchResponse = await fetch((fichier as CustomFile).uri);
      const blob = await fetchResponse.blob();
      const file = new File([blob], fichier.name || 'image.jpg', {
        type: fichier.type || 'image/jpeg'
      });
      formData.append('document', file);
    } else {
      formData.append('document', fichier as File);
    }

    return axiosInstance.post('/personnel/personnel/documents', formData)
      .then((res) => res.data);
  }

  public static deleteDocument = async (identifiantDocument: number): Promise<void> => {
    return axiosInstance.delete(`/personnel/personnel/documents/${identifiantDocument}`);
  }
}
